import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { CreateFlightPlanInput, CreateFlightPlanResponse, FlightPlanRequest } from "./flight-plan-types";
import { clerkClient } from "@/App";

const BASE_URL = "/flight-plans";

export const useCreateFlightPlanMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateFlightPlanInput) => {
      const authToken = await clerkClient.session?.getToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }
      const response = await api.post<CreateFlightPlanResponse>(BASE_URL, data, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flight-plans"] });
    },
  });
};

export const useCancelFlightPlanMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const authToken = await clerkClient.session?.getToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }
      const response = await api.post<FlightPlanRequest>(
        `${BASE_URL}/${id}/cancel`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      return response.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["flight-plans"] });
      queryClient.invalidateQueries({ queryKey: ["flight-plans", id] });
    },
  });
};

export const useFlightPlansQuery = () => {
  return useQuery({
    queryKey: ["flight-plans"],
    queryFn: async () => {
      const authToken = await clerkClient.session?.getToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }
      const response = await api.get<FlightPlanRequest[]>(BASE_URL, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return response.data;
    },
  });
};

export const useFlightPlanQuery = (id: string) => {
  return useQuery({
    queryKey: ["flight-plans", id],
    queryFn: async () => {
      const authToken = await clerkClient.session?.getToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }
      const response = await api.get<FlightPlanRequest>(`${BASE_URL}/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return response.data;
    },
    enabled: !!id,
  });
};
